import { useNavigate } from "@solidjs/router";
import { createContext, createSignal, useContext, type Accessor, type JSX } from "solid-js";
import {
  createProjectFromFile,
  IMPORT_STAGE_LABELS,
  type ImportStage,
} from "@/core/media/index.ts";
import { randomId } from "@/core/model/index.ts";
import { useApp } from "./AppContext.tsx";
import { useToast } from "@/ui/soluid/index.ts";

export type FileImporter = {
  readonly open: (files: readonly File[]) => Promise<void>;
  readonly busy: Accessor<boolean>;
  /** What the import is doing right now, ready to show as-is. */
  readonly status: Accessor<string | null>;
};

/**
 * One path from "a file arrived" to "the editor is open", shared by the drop zone on the
 * home screen and by a drop anywhere else in the window.
 */
export function createFileImporter(): FileImporter {
  const app = useApp();
  const navigate = useNavigate();
  const toast = useToast();
  const [busy, setBusy] = createSignal(false);
  const [stage, setStage] = createSignal<ImportStage | null>(null);

  return {
    busy,
    status: () => {
      const current = stage();
      return current === null ? null : IMPORT_STAGE_LABELS[current];
    },
    async open(files) {
      const file = files[0];
      const storage = app.storage();
      if (!file) return;
      if (!storage) {
        toast.add({
          message: "保存領域を準備しています。少し待ってからもう一度どうぞ",
          variant: "warning",
        });
        return;
      }
      if (files.length > 1) {
        toast.add({ message: `いまは 1 つずつ開けます。${file.name} を開きます`, variant: "info" });
      }

      setBusy(true);
      setStage("reading");
      try {
        const project = await createProjectFromFile(file, {
          blobs: storage.blobs,
          newId: randomId,
          now: () => new Date().toISOString(),
          onStage: setStage,
        });
        await storage.projects.save(project);
        app.updatePreferences({ lastProjectId: project.id });
        navigate(`/edit/${project.id}`);
      } catch (error) {
        toast.add({
          message: error instanceof Error ? error.message : "このファイルは開けませんでした",
          variant: "danger",
          duration: 0,
        });
      } finally {
        setBusy(false);
        setStage(null);
      }
    },
  };
}

const ImportContext = createContext<FileImporter>();

/** One importer for the whole app, so a window drop and the home drop zone agree. */
export function ImportProvider(props: { children: JSX.Element }): JSX.Element {
  const importer = createFileImporter();
  return <ImportContext.Provider value={importer}>{props.children}</ImportContext.Provider>;
}

export function useImporter(): FileImporter {
  const importer = useContext(ImportContext);
  if (!importer) throw new Error("ImportProvider の外で useImporter が呼ばれました");
  return importer;
}
